var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import Bowser from "bowser";
import { filterXSS } from "xss";
import scrollIntoView from "scroll-into-view";
import { snakeCase, merge, get, debounce, throttle } from "lodash-es";
const escapeRegExp = (text) => text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
function mapBy(key, arr) {
  return arr.reduce((acc, val) => (acc[val[key]] = val) && acc, {});
}
const snakedKeysCache = /* @__PURE__ */ Object.create(null);
function getSnakedKeyFactory(staticPart, interceptor) {
  staticPart = snakeCaseKeepDots(staticPart);
  if (!snakedKeysCache[staticPart]) {
    snakedKeysCache[staticPart] = /* @__PURE__ */ Object.create(null);
  }
  const staticPartsCache = snakedKeysCache[staticPart];
  return (...dynamicParts) => {
    if (interceptor) {
      dynamicParts = interceptor(dynamicParts);
    }
    const dynamicPartHash = dynamicParts.toString();
    if (!staticPartsCache[dynamicPartHash]) {
      dynamicParts = dynamicParts.filter((part) => part !== void 0);
      staticPartsCache[dynamicPartHash] = staticPart + "." + dynamicParts.map((part) => snakeCaseKeepDots(part)).join(".");
    }
    return staticPartsCache[dynamicPartHash];
  };
}
function snakeCaseKeepDots(input) {
  return input.split(".").map((part) => snakeCase(part)).join(".");
}
function flatten(source, delimiter = ".", isValue, skip) {
  const result = /* @__PURE__ */ Object.create(null);
  (function flat(obj, stack) {
    Object.keys(obj).forEach(function(key) {
      const path = stack.concat([key]);
      const val = obj[key];
      if (skip && skip(key, val)) {
        return;
      }
      if (typeof val === "object" && (!isValue || !isValue(val, key))) {
        flat(val, path);
      } else {
        result[path.join(delimiter)] = val;
      }
    });
  })(source, []);
  return result;
}
const byteUnits = ["B", "kB", "MB", "GB", "TB"];
function byteConvert(num, currentUnit = "B") {
  const unitIndex = byteUnits.indexOf(currentUnit);
  if (unitIndex === -1) {
    throw new Error(
      'Unknown byte unit: "' + currentUnit + '" expects one of: ' + byteUnits.join(", ")
    );
  }
  if (num >= 1024) {
    const higherUnit = byteUnits[unitIndex + 1];
    if (!higherUnit) {
      return num + currentUnit;
    }
    return byteConvert(Math.floor(num / 1024), higherUnit);
  } else if (num < 1) {
    const lowerUnit = byteUnits[unitIndex - 1];
    if (!lowerUnit) {
      return num + currentUnit;
    }
    return byteConvert(Math.floor(num * 1024), lowerUnit);
  }
  return num + currentUnit;
}
const customValuesCompare = (values, A, B, dir) => {
  const position = values.indexOf(A);
  const position2 = values.indexOf(B);
  if (position2 !== -1 && position !== -1) {
    return (position < position2 ? -1 : 1) * dir;
  }
  if (position !== -1) {
    return 1 * dir;
  }
  if (position2 !== -1) {
    return -1 * dir;
  }
  return dir;
};
function jsonFilter(value) {
  return JSON.stringify(value, null, 2);
}
function cardHash(value) {
  if (!value) {
    return value;
  }
  value = value.replace(/\s/g, "");
  return value.substr(0, 4) + " **** **** " + value.substr(-4);
}
function assign(...args) {
  return Object.assign.apply(Object, [{}, ...args]);
}
function evaluate(attribute) {
  if (typeof attribute === "function") {
    return attribute();
  }
  return attribute;
}
const emailPattern = /^(?:[^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*|"[^\n"]+")@(?:[^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,63}$/i;
const webPattern = /^(\*\.)*?([A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+)$/i;
const isBrowser = typeof window !== "undefined";
const bowser = isBrowser ? Bowser.getParser(window.navigator.userAgent) : null;
const _isOutdatedBrowser = bowser == null ? void 0 : bowser.satisfies({
  ie: "<20",
  safari: "<7",
  chrome: "<40",
  firefox: "<60",
  android: "<4",
  iOS: {
    firefox: "<13"
  }
});
function getCsrfToken() {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute("content");
  }
  return null;
}
const _isIE = bowser == null ? void 0 : bowser.satisfies({
  ie: ">=1"
});
function isOutdatedBrowser() {
  return !!_isOutdatedBrowser;
}
function isIE() {
  return !!_isIE;
}
function getCookieValue(name) {
  const cookie = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  if (cookie) {
    return cookie.split("=")[1];
  }
  return "";
}
function docReady(fn) {
  if (document.readyState === "complete" || document.readyState === "interactive") {
    setTimeout(fn, 1);
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}
const emphasiseQuery = (text, patern) => {
  const filterRE = new RegExp(escapeRegExp(patern), "gi");
  return filterXSS(text.replace(filterRE, (s) => `<b>${s}</b>`));
};
const simpleWrapperCmp = (element = "div") => ({
  render: function(h) {
    return h(element, this.$slots.default);
  }
});
function scrollToElement(el, config = {}, cb) {
  if (!el) {
    return null;
  }
  config = merge(
    {
      // cofiguration ref: https://www.npmjs.com/package/scroll-into-view#how
      time: 200
    },
    config
  );
  const cancel = scrollIntoView(el, config, cb);
  return {
    cancel,
    el
  };
}
function scrollToTop() {
  if (isBrowser) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }
}
function getQueryParams(url) {
  if (!url) {
    if (!isBrowser) {
      return {};
    }
    url = window.location.href;
  }
  const params = {};
  const parser = document.createElement("a");
  parser.href = url;
  const query = parser.search.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=");
    params[pair[0]] = decodeURIComponent(pair[1]);
  }
  return params;
}
function replacePlaceholders(str, params) {
  return urlTemplate(str, params);
}
const urlTemplate = (template, data, fallback) => template && template.replace(
  /:([.\w]+)/g,
  (m, param) => get(data, param) || get(fallback, param) || `:${param}`
);
function redirect(url, $event) {
  const newTab = $event ? $event.ctrlKey || $event.metaKey : false;
  const topmostWindow = window.top || window;
  topmostWindow.open(
    url,
    newTab ? "_blank" : "_self"
  );
  if (topmostWindow.location.href.startsWith(url.split("#")[0]) && url.includes("#")) {
    topmostWindow.location.reload();
  }
}
const deemphasiseQuery = (text) => {
  return text.replace(/<b>|<\/b>/g, "");
};
function ResizeObserverEntryPolyfill() {
  ["borderBoxSize", "contentBoxSize"].forEach((prop) => {
    if (window.ResizeObserver && !ResizeObserverEntry.prototype.hasOwnProperty(prop)) {
      Object.defineProperty(ResizeObserverEntry.prototype, prop, {
        get: function() {
          return calcDimensions(this.target, prop);
        }
      });
    }
  });
}
function calcDimensions(element, prop) {
  const styles = getComputedStyle(element);
  const width = parseFloat(styles.width);
  const height = parseFloat(styles.height);
  const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
  const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
  const borderXWidth = parseFloat(styles.borderLeftWidth) + parseFloat(styles.borderRightWidth);
  const borderYWidth = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
  let inlineSize = 0;
  let blockSize = 0;
  switch (prop) {
    case "borderBoxSize":
      if (styles.boxSizing === "border-box") {
        inlineSize = width;
        blockSize = height;
      } else {
        inlineSize = width + paddingX + borderXWidth;
        blockSize = height + paddingY + borderYWidth;
      }
      break;
    case "contentBoxSize":
      if (styles.boxSizing === "border-box") {
        inlineSize = width - paddingX - borderXWidth;
        blockSize = height - paddingY - borderYWidth;
      } else {
        inlineSize = width;
        blockSize = height;
      }
      break;
  }
  return [{ inlineSize, blockSize }];
}
const handlers$1 = /* @__PURE__ */ new Map();
const wsClickOutside = {
  bind: (containerElm, binding, vnode) => {
    const handler = (event) => {
      if (containerElm !== event.target && !containerElm.contains(event.target) && (document.contains(event.target) || !searchInHistory(event, containerElm))) {
        vnode.context[binding.expression](event);
      }
    };
    handlers$1.set(containerElm, handler);
    document.body.addEventListener("click", handler);
  },
  unbind: (containerElm) => {
    document.body.removeEventListener("click", handlers$1.get(containerElm));
    handlers$1.delete(containerElm);
  }
};
function searchInHistory(event, containerElm) {
  var _a;
  const history = (_a = event.composedPath) == null ? void 0 : _a.call(event);
  if (!(history == null ? void 0 : history.includes)) {
    return false;
  }
  return history.includes(containerElm);
}
const handlers = /* @__PURE__ */ new Map();
const wsAnyClick = {
  bind: (containerElm, binding, vnode) => {
    const handler = (event) => {
      vnode.context[binding.expression](event);
    };
    handlers.set(containerElm, handler);
    document.body.addEventListener("click", handler);
  },
  unbind: (containerElm) => {
    document.body.removeEventListener("click", handlers.get(containerElm));
    handlers.delete(containerElm);
  }
};
function getClosestParentWithMatchingSelector(element, selector) {
  let currentElement = element;
  while (currentElement) {
    if (currentElement.matches(selector)) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }
  return null;
}
var Country = /* @__PURE__ */ ((Country2) => {
  Country2["Sweden"] = "se";
  Country2["Slovakia"] = "sk";
  Country2["Czechia"] = "cz";
  Country2["Hungary"] = "hu";
  return Country2;
})(Country || {});
var Company = /* @__PURE__ */ ((Company2) => {
  Company2["WEBSUPPORT"] = "ws";
  Company2["LOOPIA"] = "lp";
  return Company2;
})(Company || {});
var CompanyId = /* @__PURE__ */ ((CompanyId2) => {
  CompanyId2["WEBSUPPORT_SK"] = "sk-ws";
  CompanyId2["SK_WEBSUPPORT_CZ"] = "cz-ws";
  CompanyId2["WEBSUPPORT_HU"] = "hu-ws";
  CompanyId2["WEBSUPPORT_SE"] = "se-ws";
  CompanyId2["SK_WEBSUPPORT_HU"] = "hu-skws";
  CompanyId2["ACTIVE_24_SK"] = "sk-a24";
  CompanyId2["ACTIVE_24_CZ"] = "cz-a24";
  CompanyId2["SK_WEBSUPPORT_AT"] = "at-ws";
  CompanyId2["LOOPIA_SE"] = "se-lp";
  CompanyId2["LOOPIA_NO"] = "no-lp";
  CompanyId2["ACTIVE_24_WE"] = "we-a24";
  return CompanyId2;
})(CompanyId || {});
const cache$1 = /* @__PURE__ */ Object.create(null);
function setItem(key, value, useCache) {
  const parsed = JSON.stringify(value);
  localStorage.setItem(key, parsed);
  if (useCache) {
    cache$1[key] = value;
  }
}
function getItem(key, useCache) {
  if (useCache && cache$1[key] !== void 0) {
    return cache$1[key];
  } else {
    try {
      let value = localStorage.getItem(key);
      value = value ? JSON.parse(value) : void 0;
      if (useCache && value !== void 0) {
        cache$1[key] = value;
      }
      return value;
    } catch (e) {
      localStorage.removeItem(key);
      return null;
    }
  }
}
function removeItem(key) {
  localStorage.removeItem(key);
  delete cache$1[key];
}
const cache = /* @__PURE__ */ Object.create(null);
function setSessionItem(key, value, useCache) {
  const parsed = JSON.stringify(value);
  sessionStorage.setItem(key, parsed);
  if (useCache) {
    cache[key] = value;
  }
}
function getSessionItem(key, useCache) {
  if (useCache && cache[key] !== void 0) {
    return cache[key];
  } else {
    try {
      let value = sessionStorage.getItem(key);
      value = value ? JSON.parse(value) : void 0;
      if (useCache && value !== void 0) {
        cache[key] = value;
      }
      return value;
    } catch (e) {
      sessionStorage.removeItem(key);
      return null;
    }
  }
}
function removeSessionItem(key) {
  sessionStorage.removeItem(key);
  delete cache[key];
}
const length = 20;
const uppercaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const lowercaseLetters = "abcdefghijklmnopqrstuvwxyz";
const numbers = "0123456789";
const symbols = "!#$%&()*+,-./:;<=>?@[]^_`{|}~";
const wishlist = uppercaseLetters + lowercaseLetters + numbers + symbols;
const isValidPassword = (password) => {
  let [hasUppercase, hasLowercase, hasNumber, hasSymbol] = [false, false, false, false];
  for (let i = 0; i < password.length; i++) {
    const currentCharacter = password.charAt(i);
    if (uppercaseLetters.includes(currentCharacter)) {
      hasUppercase = true;
    }
    if (lowercaseLetters.includes(currentCharacter)) {
      hasLowercase = true;
    }
    if (numbers.includes(currentCharacter)) {
      hasNumber = true;
    }
    if (symbols.includes(currentCharacter)) {
      hasSymbol = true;
    }
  }
  return hasUppercase && hasLowercase && hasNumber && hasSymbol;
};
function generatePassword() {
  let password;
  do {
    password = Array.from(crypto.getRandomValues(new Uint32Array(length))).map((x) => wishlist[x % wishlist.length]).join("");
  } while (!isValidPassword(password));
  return password;
}
class PromisesQueue {
  constructor(options = {}) {
    __publicField(this, "queue", []);
    __publicField(this, "pendingPromises", []);
    __publicField(this, "workingOnPromise", false);
    __publicField(this, "executeAllImmediately", false);
    // don't queue, run all promises immediately
    __publicField(this, "options");
    this.options = options;
  }
  enqueue(promise) {
    return new Promise((resolve, reject) => {
      this.queue.push({
        promise,
        resolve,
        reject
      });
      this.dequeue();
    });
  }
  // performs next step - take next promise in queue
  dequeue() {
    if (this.workingOnPromise && !this.executeAllImmediately) {
      return false;
    }
    const item = this.queue.shift();
    if (!item) {
      return false;
    } else {
      this.workingOnPromise = true;
      if (this.executeAllImmediately) {
        this.dequeue();
      }
      const currentPromise = item.promise();
      this.pendingPromises.push(
        currentPromise.then((value) => {
          item.resolve(value);
        }).catch((err) => {
          item.reject(err);
        }).finally(() => {
          var _a, _b;
          this.workingOnPromise = false;
          this.pendingPromises.splice(
            this.pendingPromises.indexOf(currentPromise),
            1
          );
          if (!this.pendingPromises.length && !this.queue.length) {
            (_b = (_a = this.options).onComplete) == null ? void 0 : _b.call(_a);
          }
          this.dequeue();
        })
      );
      return true;
    }
  }
}
function Debounce(delay = 300) {
  return (target, prop) => {
    return {
      configurable: true,
      enumerable: false,
      value: debounce(target[prop], delay)
    };
  };
}
function Throttle(delay = 300, options) {
  return (target, prop) => {
    return {
      configurable: true,
      enumerable: false,
      value: throttle(target[prop], delay, options)
    };
  };
}
function pushDataTrackingToDataLayer(el, data) {
  if (typeof window.pushDataTrackingToDataLayer === "function") {
    window.pushDataTrackingToDataLayer(el, data);
  }
}
export {
  Company,
  CompanyId,
  Country,
  Debounce,
  PromisesQueue,
  ResizeObserverEntryPolyfill,
  Throttle,
  assign,
  byteConvert,
  byteUnits,
  cardHash,
  customValuesCompare,
  deemphasiseQuery,
  docReady,
  emailPattern,
  emphasiseQuery,
  escapeRegExp,
  evaluate,
  flatten,
  generatePassword,
  getClosestParentWithMatchingSelector,
  getCookieValue,
  getCsrfToken,
  getItem,
  getQueryParams,
  getSessionItem,
  getSnakedKeyFactory,
  isBrowser,
  isIE,
  isOutdatedBrowser,
  jsonFilter,
  mapBy,
  pushDataTrackingToDataLayer,
  redirect,
  removeItem,
  removeSessionItem,
  replacePlaceholders,
  scrollToElement,
  scrollToTop,
  setItem,
  setSessionItem,
  simpleWrapperCmp,
  snakeCaseKeepDots,
  urlTemplate,
  webPattern,
  wsAnyClick,
  wsClickOutside
};
//# sourceMappingURL=index.es.js.map
